import type { components } from './generated/schema';

/**
 * Typy API — odvozené z generovaného schématu, ne psané ručně.
 *
 * `generated/schema.d.ts` se generuje ze Swaggeru backendu
 * (`npm run api:types` / `api:types:local`) a NEEDITUJE se ručně.
 * Tenhle soubor je nad ním jen tenká vrstva s čitelnými aliasy — díky ní
 * se při změně kontraktu nemusí přepisovat importy po celé aplikaci.
 *
 * Rozdíl proti `domain/types.ts`: tady je to, CO POSÍLÁ SERVER.
 * Tam je to, S ČÍM PRACUJE UI. Mezi nimi stojí `mapPackage.ts`.
 */
type Schemas = components['schemas'];

// --- odpovědi ---
export type PackageInfoDto = Schemas['PackageInfoResponse'];
export type OrderPackageDto = Schemas['OrderPackage'];
export type SessionDto = Schemas['Session'];
export type AuthResponseDto = Schemas['AuthResponse'];
export type SessionContextResponseDto = Schemas['SessionContextResponse'];
export type UserPreferencesResponseDto = Schemas['UserPreferencesResponse'];
export type ScanLogResponseDto = Schemas['ScanLogResponse'];
export type TransferConfirmResponseDto = Schemas['TransferConfirmResponse'];
export type ReportIssueResponseDto = Schemas['ReportIssueResponse'];
export type WeightUpdateResponseDto = Schemas['WeightUpdateResponse'];
export type ApiErrorDto = Schemas['ApiErrorResponse'];
export type RouteListResponseDto = Schemas['RouteListResponse'];
export type RouteListItemDto = Schemas['RouteListItem'];
export type RouteDetailResponseDto = Schemas['RouteDetailResponse'];
export type RouteShipmentDto = Schemas['RouteShipment'];
export type RouteParcelDto = Schemas['RouteParcel'];

/** `GET /app/v2/scanner/overview` — rozskenované zásilky napříč trasami. */
export type OverviewResponseDto = Schemas['OverviewResponse'];
export type OverviewShipmentDto = Schemas['OverviewShipment'];

// --- požadavky ---
export type AuthRequestDto = Schemas['AuthRequest'];
export type PackageCodeRequestDto = Schemas['PackageCodeRequest'];
export type FindByOrderRequestDto = Schemas['FindByOrderRequest'];
export type ScanLogRequestDto = Schemas['ScanLogRequest'];
export type TransferConfirmRequestDto = Schemas['TransferConfirmRequest'];
export type ReportIssueRequestDto = Schemas['ReportIssueRequest'];
export type UserPreferencesRequestDto = Schemas['UserPreferencesRequest'];

/**
 * Výčty ze schématu.
 *
 * Tohle je ten rozdíl, kvůli kterému stálo za to trvat na enumech na backendu:
 * `ShipmentStatusCode` je union osmi hodnot, takže neošetřený stav neprojde
 * překladem. Kdyby to byl `string`, zjistili bychom to až v hale.
 */
export type ShipmentStatusCode = Schemas['ShipmentStatusCode'];
export type TransferDirection = Schemas['TransferDirectionCode'];
export type TemperatureMode = Schemas['TemperatureModeCode'];
export type ReportReasonCode = Schemas['ReportReasonCode'];
export type UserLanguageCode = Schemas['UserLanguageCode'];
/** `WAREHOUSE` | `DRIVER` | `MASTER` — přehled tras vidí jen `MASTER`. */
export type DeviceRoleCode = Schemas['DeviceRoleCode'];

/** Kódy chyb, na které aplikace reaguje. Ve schématu je `error` volný string. */
export const API_ERRORS = {
  packageNotFound: 'PACKAGE_NOT_FOUND',
  orderNotFound: 'ORDER_NOT_FOUND',
  validationFailed: 'VALIDATION_FAILED',
  noteRequired: 'NOTE_REQUIRED',
  unauthorized: 'UNAUTHORIZED',
  transferNotApplicable: 'TRANSFER_NOT_APPLICABLE',
} as const;

export type ApiErrorKind =
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NETWORK'
  | 'SERVER'
  | 'CONFLICT';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  /** Kód z těla odpovědi (`error`), pokud dorazilo. */
  readonly code: string | null;
  /** Jen pro log a debug panel, ne pro obrazovku. */
  readonly detail: string | null;
  /** HTTP status. Fronta podle něj rozlišuje dočasnou chybu (5xx) od trvalé (4xx). */
  readonly status: number | null;

  constructor(
    kind: ApiErrorKind,
    detail: string | null = null,
    status: number | null = null,
    code: string | null = null,
  ) {
    super(`${kind}${code ? ` (${code})` : ''}${detail ? `: ${detail}` : ''}`);
    this.name = 'ApiError';
    this.kind = kind;
    this.detail = detail;
    this.status = status;
    this.code = code;
  }
}
